import React, { useMemo } from "react";
import { Input, Select } from "@pd/pd-ui-kit";
import { Controller } from "react-hook-form";
import { useTranslation } from "react-i18next";

import FormField from "~/pages/BaseStaff/EmployeeProfileEditor/EmployeeProfileForm/sections/FormField";
import { useGetDealersForSelect } from "~/entity/Dealers";
import { getEmailVerificationOptions } from "~/pages/BaseStaff/EmployeeProfileEditor/EmployeeProfileForm/model/options";
import { TFileInfo } from "~/pages/BaseStaff/EmployeeProfileEditor/EmployeeProfileForm/model/types";
import { FileFieldController } from "~/shared/ui/Fields/FileField";

import { IDealerRowProps, TDealerErrors } from "./types";

import s from "~/pages/BaseStaff/EmployeeProfileEditor/EmployeeProfileForm/EmployeeProfileForm.less";

type TDealersSelect = ReturnType<typeof useGetDealersForSelect>;

interface IOrganizationFieldsRowProps extends IDealerRowProps {
  dealerErrors?: TDealerErrors;
  disabled?: boolean;
  organizationDisabled?: boolean;
  dealers: TDealersSelect["options"];
  onSearchDealer: TDealersSelect["onSearch"];
  onScrollBottomDealers?: TDealersSelect["onScrollBottom"];
  onOrganizationTypeChange?: () => void;
  consentFile?: TFileInfo | null;
  emailRequired?: boolean;
}

const OrganizationFieldsRow = ({
  control,
  dealerPrefix,
  dealerErrors,
  disabled,
  organizationDisabled,
  dealers,
  onSearchDealer,
  onScrollBottomDealers,
  onOrganizationTypeChange,
  consentFile,
  emailRequired = true,
}: IOrganizationFieldsRowProps) => {
  const { t } = useTranslation();

  const organizationTypeOptions = useMemo(
    () => [
      { value: "DEALER", label: t("employee.dealerCenter") },
      { value: "PARTNER", label: t("employee.partner") },
    ],
    [t],
  );
  const emailVerificationOptions = useMemo(
    () => getEmailVerificationOptions(t),
    [t],
  );
  const dataConsentTemplateLink = useMemo(
    () => ({
      label: t("employee.templates_title").trim(),
      href: "/userfiles/template2.docx",
    }),
    [t],
  );

  return (
    <>
      <div className={s.grid4}>
        <FormField label={t("employee.organizationType")}>
          <Controller
            name={`${dealerPrefix}.organizationType`}
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Select
                labelInValue
                value={
                  organizationTypeOptions.find(
                    (option) => option.value === (value || "DEALER"),
                  ) || organizationTypeOptions[0]
                }
                options={organizationTypeOptions}
                onBlur={onBlur}
                onChange={(option) => {
                  onChange(option?.value || "DEALER");
                  onOrganizationTypeChange?.();
                }}
                disabled={organizationDisabled}
              />
            )}
          />
        </FormField>
      </div>

      <div className={s.grid4}>
        <FormField
          label={t("employee.organization")}
          error={dealerErrors?.dealer?.message}
          required
        >
          <Controller
            name={`${dealerPrefix}.dealer`}
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Select
                labelInValue
                showSearch
                value={value}
                options={dealers}
                filterOption={false}
                onBlur={onBlur}
                onSearch={onSearchDealer}
                onPopupScroll={onScrollBottomDealers}
                onChange={onChange}
                disabled={organizationDisabled}
                status={dealerErrors?.dealer ? "error" : undefined}
              />
            )}
          />
        </FormField>

        <FormField label={t("employee.email")} required={emailRequired}>
          <Controller
            name={`${dealerPrefix}.email`}
            control={control}
            render={({ onChange, value, onBlur }) => (
              <Input
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onBlur={onBlur}
                disabled={disabled}
                status={dealerErrors?.email ? "error" : undefined}
                errorText={dealerErrors?.email?.message}
              />
            )}
          />
        </FormField>

        <FormField label={t("employee.mainEmailStatus")}>
          <Controller
            name={`${dealerPrefix}.emailVerification`}
            control={control}
            render={({ value }) => {
              const statusLabel =
                emailVerificationOptions.find(
                  (option) => option.value === value,
                )?.label ?? "";

              return <Input value={statusLabel} disabled />;
            }}
          />
        </FormField>

        <FormField label={t("employee.approval")} required>
          <FileFieldController
            name={`${dealerPrefix}.dataConsentId`}
            control={control}
            accept=".png,.jpg,.jpeg,.doc,.pdf,.docx"
            maxSizeMb={5}
            disabled={disabled}
            templateLink={dataConsentTemplateLink}
            error={dealerErrors?.dataConsentId?.message}
            initialFile={
              consentFile
                ? {
                    id: consentFile.id,
                    name: consentFile.name || t("employee.approval"),
                    link: consentFile.link || "",
                  }
                : null
            }
          />
        </FormField>
      </div>
    </>
  );
};

export default OrganizationFieldsRow;
